import React, { useState } from "react";

import { Smartphone, Laptop, Monitor, LogOut, Clock, MapPin } from "lucide-react";

import { observer } from "mobx-react-lite";

import { useStore } from "../../helpers";
import { ConfirmationModal } from "../../features/task-manager-v2/components/ConfirmationModal";

interface Props {
    isUserManagement?: boolean;

    activeDeviceUser?: any;
}

const getIcon = (type?: string) => {
    if (type === "mobile") {
        return <Smartphone size={20} />;
    }

    if (type === "desktop") {
        return <Laptop size={20} />;
    }

    return <Monitor size={20} />;
};

const formatDate = (date?: string) => {
    if (!date) return "-";

    return new Date(date).toLocaleString("en-IN", {
        day: "numeric",
        month: "short",
        hour: "numeric",
        minute: "2-digit",
    });
};

const getBrowserName = (browser?: string) => {
    if (!browser) return "Browser";

    if (browser.includes("Chrome")) return "Chrome";

    if (browser.includes("Firefox")) return "Firefox";

    if (browser.includes("Safari")) return "Safari";

    return "Browser";
};

export const ActiveDevices = observer(
    ({
        isUserManagement = false,
        activeDeviceUser,
    }: Props) => {
        const { userStore } = useStore();
        const [showLogoutAllModal, setShowLogoutAllModal] = useState(false);

        const devices = userStore.active_device || [];

        const handleLogoutDevice = async (id: string) => {
            try {
                let response;

                if (isUserManagement) {
                    response = await userStore.revokeuserdevice(activeDeviceUser?.id, [id]);
                } else {
                    response = await userStore.revokedevices(id);
                }

                if (response?.ok) {
                    userStore.set_active_devices(devices.filter((d: any) => d.id !== id));
                }
            } catch (e) {
                console.log(e);
            }
        };

        // const handleLogoutAll = async () => {
        //     try {
        //         let response;

        //         const current = devices.find((d: any) => d.is_current);

        //         if (!current) {
        //             return;
        //         }

        //         response = await userStore.logoutall(current.id);

        //         if (response?.ok) {
        //             userStore.set_active_devices(
        //                 isUserManagement ? [] : devices.filter((d: any) => d.is_current)
        //             );
        //         }
        //     } catch (e) {
        //         console.log(e);
        //     }
        // };

        const handleLogoutAll = async () => {
            try {
                const response = await userStore.logoutall();
                console.log("Logout all response:", response);
                if (response?.ok) {
                    userStore.set_active_devices(devices.filter((d: any) => d.is_current));
                }
            } catch (e) {
                console.error("Logout all error:", e);
            }
        };

        return (
            <div className="flex flex-col">
                <div className="mb-6">
                    <h2 className="text-xl font-semibold">Active Devices</h2>
                    <p className="text-sm text-gray-500">Logged-in sessions</p>
                </div>

                <div className="space-y-4">
                    {!devices.length && (
                        <div className="py-10 text-center text-gray-400">No active devices</div>
                    )}

                    {devices.map((device: any) => (
                        <div
                            key={device.id}
                            className="rounded-3xl border border-gray-100 bg-white p-5 shadow-sm"
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex gap-4">
                                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600">
                                        {getIcon(device?.more?.devicetype)}
                                    </div>

                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h3 className="font-semibold">
                                                {getBrowserName(device?.more?.browser)}
                                            </h3>

                                            {device.is_current && (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 px-3 py-1 text-[11px] font-semibold text-white">
                                                    <span className="h-2 w-2 rounded-full bg-white" />
                                                    Current Device
                                                </span>
                                            )}
                                        </div>

                                        <div className="text-sm text-gray-500">{device.platform}</div>

                                        <div className="mt-2 flex items-center gap-2 text-xs text-gray-400">
                                            <Clock size={12} />
                                            Active • {formatDate(device.issued)}
                                        </div>

                                        <div className="mt-1 flex items-center gap-2 text-xs">
                                            <MapPin size={12} className="text-gray-400" />

                                            {device.location?.latitude ? (
                                                <a
                                                    href={`https://www.google.com/maps?q=${device.location.latitude},${device.location.longitude}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-indigo-600 hover:underline"
                                                >
                                                    View Location
                                                </a>
                                            ) : (
                                                <span className="text-gray-400">No Location</span>
                                            )}
                                        </div>

                                        <div className="mt-1 text-xs text-gray-400">{device.timezone}</div>

                                        <div className="mt-1 flex items-center gap-2 text-xs text-gray-400">
                                            <Clock size={12} />
                                            Expires • {formatDate(device.expiry_datetime)}
                                        </div>

                                        <div className="mt-1 text-xs text-gray-400">
                                            {device?.more?.devicetype}
                                        </div>
                                    </div>
                                </div>

                                {!device.is_current && (
                                    <button
                                        onClick={() => handleLogoutDevice(device.id)}
                                        className="rounded-xl border border-red-100 bg-red-50 p-3 text-red-600 transition hover:bg-red-100"
                                    >
                                        <LogOut size={18} />
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {devices.length > 1 && (
                    <button
                        onClick={() => setShowLogoutAllModal(true)}
                        className="mt-6 w-full rounded-2xl bg-red-500 py-3 font-medium text-white transition hover:bg-red-600"
                    >
                        {isUserManagement ? "Logout all User Devices" : "Logout All Other Devices"}
                    </button>
                )}

                <ConfirmationModal
                    isOpen={showLogoutAllModal}
                    onClose={() => setShowLogoutAllModal(false)}
                    onConfirm={handleLogoutAll}
                    title="Logout All Devices"
                    message="Are you sure you want to logout all devices?"
                    confirmText="Logout"
                    cancelText="Cancel"
                    confirmButtonColor="error"
                />
            </div>
        );
    }
);

export default ActiveDevices;
